import React from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Form, Input } from '@flightaware/ui-components';

import { User } from 'models/user';
import { createUser, updateUser } from 'api/users';
import { ManageUserFormData } from 'models/manageUserFormData';
import { capitalizeString, parseCommaSeparatedFilterValue } from 'utils/format';
import { objectDiff } from 'utils/object';

import ManageUserDashboards from 'components/ManageUserDashboards';
import { useAuth } from 'services/authentication';

interface ManageUserProps {
    user: User;
    action: 'create' | 'update';
}

const userSchema = (action: ManageUserProps['action']) => {
    return yup.object().shape({
        id:
            action === 'create'
                ? yup.number().typeError('Please enter a number.').required('Field is required.')
                : yup.string().notRequired(),
        aeroApiKey: yup
            .string()
            .matches(/^[a-zA-Z0-9]*$/, 'The AeroAPI key must be alphanumeric.')
            .required('Field is required.')
            .min(32, 'The AeroAPI key must be at least 32 characters long.'),
        minimumPollDelay: yup
            .number()
            .typeError('Please enter a number.')
            .required('Field is required.')
            .min(30, (rule) => `Refresh rate must be at least ${rule.min}.`)
            .integer(),
        filterOperators: yup.mixed(),
        filterAirports: yup.mixed(),
        filterRegistrations: yup.mixed(),
    });
};

const useUpdateUser = (userId: string) => {
    const queryClient = useQueryClient();

    return useMutation<User, Error, Partial<User>>((changes) => updateUser(userId, changes), {
        onSuccess: () => {
            // @todo  Consider other queries that need to be invalidated
            queryClient.resetQueries({ queryKey: ['users'] });
        },
    });
};

const useCreateUser = () => {
    const queryClient = useQueryClient();

    return useMutation<User, Error, Partial<User>>((newUser) => createUser(newUser), {
        onSuccess: () => {
            queryClient.resetQueries({ queryKey: ['users'] });
        },
    });
};

// This returns a default object that prepopulates the new user form
const getUserFromFormData = (formData: ManageUserFormData): Partial<User> => {
    const user: Partial<User> = {
        id: formData.id,
        permissions: {
            role: undefined,
            minimumPollDelay: formData.minimumPollDelay,
            filters: {
                operators:
                    formData.filterOperators !== undefined
                        ? formData.filterOperators?.map((operator) => operator.value) || []
                        : parseCommaSeparatedFilterValue(formData.filterOperatorsAny),
                airports:
                    formData.filterAirports !== undefined
                        ? formData.filterAirports?.map((airport) => airport.value) || []
                        : parseCommaSeparatedFilterValue(formData.filterAirportsAny),
                registrations:
                    formData.filterRegistrations !== undefined
                        ? formData.filterRegistrations?.map((registration) => registration.value) || []
                        : parseCommaSeparatedFilterValue(formData.filterRegistrationsAny),
            },
            aeroApiKey: formData.aeroApiKey,
        },
    };

    return user;
};

export default function ManageUser(props: ManageUserProps) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { mutate } = props.action === 'update' ? useUpdateUser(props.user.id) : useCreateUser();

    const isAdmin = user?.permissions.role === 'admin';
    const showManageUserDashboards = isAdmin && props.action === 'update';

    return (
        <div className="manage fixed-width" data-testid="manage-user">
            <h1>{capitalizeString(props.action)} user</h1>
            <div className={showManageUserDashboards ? 'half-width' : ''}>
                <Form
                    onSubmit={(data: ManageUserFormData) => {
                        const newUser = getUserFromFormData(data);
                        const changes = props.action === 'update' ? objectDiff(props.user, newUser) : newUser;
                        mutate(changes, {
                            onError: (error) => toast.error(`Error saving user: ${error.message}`),
                            onSuccess: () => {
                                toast.success('User saved');
                                navigate('/admin');
                            },
                        });
                    }}
                    onReset={() => navigate(-1)}
                    validationSchema={userSchema(props.action)}
                    buttonPosition="left"
                    submitButton={{ show: true, text: 'Save', size: 'large' }}
                    resetButton={{ show: true, text: 'Cancel', size: 'large' }}
                    customClass="manage-user__form"
                >
                    <Input
                        type="text"
                        name="id"
                        label="FA user ID"
                        placeholder="123456789"
                        defaultValue={props.user.id}
                        disabled={props.action === 'update'}
                    />
                    <Input
                        type="text"
                        name="aeroApiKey"
                        label="AeroAPI key"
                        placeholder="abcdefghijklmnopqrstuvwxyz123456"
                        defaultValue={props.user.permissions.aeroApiKey}
                    />
                    <Input
                        type="numeric"
                        name="minimumPollDelay"
                        label="Refresh rate (in seconds)"
                        placeholder="30"
                        defaultValue={props.user.permissions.minimumPollDelay}
                    />
                    <Input
                        type="text"
                        name="filterOperatorsAny"
                        label="Operators to show"
                        placeholder="EZY, EZS"
                        defaultValue={(props.user.permissions.filters.operators || []).join(', ')}
                    />
                    <Input
                        type="text"
                        name="filterAirportsAny"
                        label="Airports to show"
                        placeholder="EGLL, KIAH"
                        defaultValue={(props.user.permissions.filters.airports || []).join(', ')}
                    />
                    <Input
                        type="text"
                        name="filterRegistrationsAny"
                        label="Registrations to show"
                        placeholder="YL-LDN, N24980"
                        defaultValue={(props.user.permissions.filters.registrations || []).join(', ')}
                    />
                </Form>
            </div>
            {showManageUserDashboards && (
                <div className={showManageUserDashboards ? 'half-width' : ''}>
                    <ManageUserDashboards user={props.user} />
                </div>
            )}
        </div>
    );
}

ManageUser.propTypes = {
    user: PropTypes.object,
};
